/**
 * Diesel Dudes — WebMCP / Agentic-Browsing front-end layer.
 *
 * Registers read-only WebMCP tools (document.modelContext) and adds declarative
 * form annotations so browser AI agents and Google's Lighthouse "Agentic
 * Browsing" audit can discover and use the storefront. Shopify already serves
 * the native commerce MCP (cart/checkout via UCP); this file adds ONLY the
 * in-page browser-interaction + Lighthouse-scored layer.
 *
 * Self-contained: no external/CDN deps, no tokens/secrets, public endpoints
 * only (/products.json, /products/<handle>.json). All tools are readOnlyHint.
 *
 * Security guardrails applied: SHOPIFY-SEC-01..10 (see SEC report). Notably:
 * explicit form allowlist + denylist, no write tools, one fetch per execute,
 * limit clamping, HTML-stripped + length-clamped annotation text, feature
 * detection that never throws on unsupported browsers.
 */
(function () {
    'use strict';

    /* ------------------------------------------------------------------
     * CONFIG — static business/contact info surfaced by get_business_info
     * and get_contact_info. Email is intentionally BLANK by default
     * (SEC F-09): filling it publishes it in page source to scrapers.
     * The snippet fallback overrides this via window.DieselDudesWebMCP.
     * ------------------------------------------------------------------ */
    var DEFAULT_CONFIG = {
        siteUrl: 'https://thedieseldudes.com',
        name: 'Diesel Dudes',
        hours: 'Mon-Fri 8am-5pm CST',
        email: '',                 // intentionally blank; fill only if a public support email is wanted
        phone: ''                  // fill only if a public support phone is wanted
    };
    var CONFIG = {};
    var override = (typeof window !== 'undefined' && window.DieselDudesWebMCP) || {};
    for (var key in DEFAULT_CONFIG) {
        if (Object.prototype.hasOwnProperty.call(DEFAULT_CONFIG, key)) {
            CONFIG[key] = (override[key] !== undefined && override[key] !== null) ? override[key] : DEFAULT_CONFIG[key];
        }
    }
    var SITE_URL = String(CONFIG.siteUrl || '').replace(/\/+$/, '');

    /* ------------------------------------------------------------------
     * Helpers
     * ------------------------------------------------------------------ */

    // Strip HTML and clamp to <=200 chars. Used on any dynamic text that
    // enters tool output or DOM annotations (SHOPIFY-SEC-05, SEC-08).
    function cleanText(s) {
        return String(s == null ? '' : s)
            .replace(/<[^>]*>/g, ' ')
            .replace(/[\r\n\t]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 200);
    }

    function joinLines(lines) {
        return lines.filter(function (l) { return l && String(l).trim(); }).join('\n');
    }

    // One fetch, JSON parse, explicit non-2xx handling. 429 surfaces as a
    // readable message (SHOPIFY-SEC-07/09 — one call per execute, handle 429).
    async function fetchJson(url) {
        var res = await fetch(url, { headers: { 'Accept': 'application/json' } });
        if (res.status === 429) {
            throw new Error('Rate limited by the store (429). Please retry shortly.');
        }
        if (!res.ok) {
            throw new Error('Request failed with status ' + res.status);
        }
        return res.json();
    }

    // Build an absolute product URL from a handle.
    function productUrl(handle) {
        return SITE_URL + '/products/' + handle;
    }

    // Normalize a Shopify /products.json product object to the agent-facing shape.
    function normalizeProduct(p) {
        var firstVariant = (p.variants && p.variants.length) ? p.variants[0] : null;
        var firstImage = (p.images && p.images.length) ? p.images[0] : null;
        return {
            title: cleanText(p.title),
            price: firstVariant && firstVariant.price != null ? String(firstVariant.price) : '',
            url: productUrl(p.handle),
            image: firstImage && firstImage.src ? firstImage.src : '',
            handle: p.handle || ''
        };
    }

    /* ------------------------------------------------------------------
     * Declarative form annotator (Lighthouse audits the DOM, not the JS
     * API — so this runs regardless of WebMCP support).
     *
     * EXPLICIT ALLOWLIST ONLY (SHOPIFY-SEC-07, SEC F-08):
     *   (a) storefront search form: form[action*="/search"] with input[name="q"]
     *   (b) footer newsletter form: a form with an email input (Shopify
     *       customer-marketing signup posts to /contact#... so we match on the
     *       email input rather than an action substring).
     *
     * EXPLICIT DENYLIST — never annotate cart/checkout/account/login/register
     * or any form containing input[type="password"] (SHOPIFY-SEC-01/06).
     *
     * NO generic/random-name fallback annotator (SEC F-08).
     * ------------------------------------------------------------------ */

    var DENY_ACTION_PATTERNS = ['/cart', '/checkout', '/account', '/login', '/register', 'password'];

    function isDenied(form) {
        // Hard block: any password field => auth/checkout-like form.
        if (form.querySelector('input[type="password"]')) return true;
        var action = (form.getAttribute('action') || '').toLowerCase();
        for (var i = 0; i < DENY_ACTION_PATTERNS.length; i++) {
            if (action.indexOf(DENY_ACTION_PATTERNS[i]) !== -1) return true;
        }
        return false;
    }

    function setIf(el, attr, val) {
        if (el && !el.hasAttribute(attr)) el.setAttribute(attr, cleanText(val));
    }

    function annotateSearchForm(form) {
        var input = form.querySelector('input[name="q"]');
        if (!input) return false;
        setIf(form, 'toolname', 'search_products');
        setIf(form, 'tooldescription', 'Search the Diesel Dudes catalog for products by keyword.');
        setIf(input, 'toolparamdescription', 'The keyword or phrase to search products for. Plain text.');
        return true;
    }

    function annotateNewsletterForm(form) {
        var emailInput = form.querySelector('input[type="email"], input[name="contact[email]"]');
        if (!emailInput) return false;
        setIf(form, 'toolname', 'subscribe_newsletter');
        setIf(form, 'tooldescription', 'Subscribe an email address to the Diesel Dudes newsletter.');
        setIf(emailInput, 'toolparamdescription', 'The email address to subscribe to the newsletter.');
        return true;
    }

    function annotateForms() {
        // (a) Search form — explicit selector.
        var searchForms = document.querySelectorAll('form[action*="/search"]');
        for (var i = 0; i < searchForms.length; i++) {
            var sf = searchForms[i];
            try {
                if (sf.hasAttribute('toolname')) continue;
                if (isDenied(sf)) continue;
                annotateSearchForm(sf);
            } catch (e) { /* never block the page */ }
        }

        // (b) Newsletter form — match by email input, denylist-guarded so we
        // never catch a login/account form that happens to have an email field.
        var allForms = document.querySelectorAll('form');
        for (var j = 0; j < allForms.length; j++) {
            var nf = allForms[j];
            try {
                if (nf.hasAttribute('toolname')) continue;   // already handled (e.g. search)
                if (isDenied(nf)) continue;
                annotateNewsletterForm(nf);
            } catch (e) { /* never block the page */ }
        }
    }

    /* ------------------------------------------------------------------
     * WebMCP tool registration.
     *
     * document.modelContext is primary (navigator.modelContext is deprecated
     * in Chrome 150 — kept only as a guarded fallback). Feature-detect and
     * bail silently on unsupported browsers (SHOPIFY-SEC-10) AFTER the form
     * annotator has run.
     * ------------------------------------------------------------------ */
    function registerTools() {
        var mc = (typeof document !== 'undefined' && document.modelContext) ||
                 (typeof navigator !== 'undefined' && navigator.modelContext);
        if (!mc || typeof mc.registerTool !== 'function') return;

        /* ----- search_products ----- */
        mc.registerTool({
            name: 'search_products',
            description: 'Search the Diesel Dudes catalog by keyword. Returns up to 25 matching products with title, price, URL, image, and handle.',
            inputSchema: {
                type: 'object',
                properties: {
                    query: { type: 'string', description: 'Keyword or phrase to search products for.' },
                    limit: { type: 'integer', description: 'Max results, default 10, max 25.', default: 10 }
                },
                required: ['query']
            },
            annotations: { readOnlyHint: true },
            execute: async function (input) {
                input = input || {};
                var q = String(input.query || '').trim().toLowerCase();
                var n = parseInt(input.limit, 10);
                if (isNaN(n) || n < 1) n = 10;
                if (n > 25) n = 25;
                try {
                    // ?query= support is unverified on the live store, so we fetch a
                    // page of products and filter client-side — works regardless.
                    var data = await fetchJson(SITE_URL + '/products.json?limit=' + n);
                    var products = (data && data.products) || [];
                    var matches = products;
                    if (q) {
                        matches = products.filter(function (p) {
                            var hay = [
                                p.title || '',
                                (p.tags || []),
                                p.vendor || '',
                                p.product_type || ''
                            ].join(' ').toLowerCase();
                            return hay.indexOf(q) !== -1;
                        });
                    }
                    return matches.slice(0, n).map(normalizeProduct);
                } catch (e) {
                    return { error: e.message };
                }
            }
        });

        /* ----- get_product ----- */
        mc.registerTool({
            name: 'get_product',
            description: 'Get details for a single Diesel Dudes product by its handle (the URL slug, e.g. "diesel-power-additive").',
            inputSchema: {
                type: 'object',
                properties: {
                    handle: { type: 'string', description: 'The product handle / URL slug.' }
                },
                required: ['handle']
            },
            annotations: { readOnlyHint: true },
            execute: async function (input) {
                input = input || {};
                var handle = String(input.handle || '').trim().toLowerCase().replace(/[^a-z0-9\-_]/g, '');
                if (!handle) return { error: 'No handle provided.' };
                try {
                    var data = await fetchJson(SITE_URL + '/products/' + handle + '.json');
                    var p = data && data.product;
                    if (!p) return { error: 'No product found for handle "' + handle + '".' };
                    var base = normalizeProduct(p);
                    base.description = cleanText(p.body_html);
                    base.vendor = cleanText(p.vendor);
                    base.product_type = cleanText(p.product_type);
                    return base;
                } catch (e) {
                    return { error: e.message };
                }
            }
        });

        /* ----- get_business_info ----- */
        mc.registerTool({
            name: 'get_business_info',
            description: 'Return general info about Diesel Dudes: name, website, and hours.',
            inputSchema: { type: 'object', properties: {} },
            annotations: { readOnlyHint: true },
            execute: async function () {
                return joinLines([
                    CONFIG.name ? 'Business: ' + CONFIG.name : '',
                    SITE_URL ? 'Website: ' + SITE_URL : '',
                    CONFIG.hours ? 'Hours: ' + CONFIG.hours : ''
                ]) || 'Business info not configured.';
            }
        });

        /* ----- get_contact_info ----- */
        mc.registerTool({
            name: 'get_contact_info',
            description: 'Return contact details for Diesel Dudes: email and phone (if published) and website.',
            inputSchema: { type: 'object', properties: {} },
            annotations: { readOnlyHint: true },
            execute: async function () {
                return joinLines([
                    CONFIG.name ? 'Business: ' + CONFIG.name : '',
                    CONFIG.email ? 'Email: ' + CONFIG.email : '',
                    CONFIG.phone ? 'Phone: ' + CONFIG.phone : '',
                    SITE_URL ? 'Website: ' + SITE_URL : ''
                ]) || 'Contact info not yet configured.';
            }
        });
    }

    /* ------------------------------------------------------------------
     * Run once on DOMContentLoaded; idempotent (toolname guards + the
     * annotator's hasAttribute checks make re-entry safe).
     * ------------------------------------------------------------------ */
    function init() {
        try { annotateForms(); } catch (e) { /* never block the page */ }
        try { registerTools(); } catch (e) { /* never block the page */ }
    }

    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            init();
        }
    }
})();
